import { detectOSInBrowser } from '@bonsai-components/detect-os';
import {
  ModModal,
  ModModalContent,
  ModModalHeader
} from '@moderneinc/design-system-components';
import { TabContext } from '@mui/lab';
import { Tab, Tabs, Typography } from '@mui/material';
import { bindDialog } from 'material-ui-popup-state';
import type { PopupState } from 'material-ui-popup-state/hooks';
import { type FunctionComponent, useState } from 'react';
import { CodeSnippet } from '../../code-snippet/code-snippet.component';
import { ModerneTabPanel } from '../../styled-components/tabs/tabs.styled';
import { Link } from '../../utilities/moderne-link/moderne-link.component';

type ModerneCliDialogProps = {
  popupState: PopupState;
  cliBaseUrl: string;
};

export const ModerneCliDialog: FunctionComponent<ModerneCliDialogProps> = ({
  popupState,
  cliBaseUrl
}) => {
  const OS = detectOSInBrowser();
  const [currentTab, setCurrentTab] = useState<string>(() =>
    OS === 'windows' ? 'windows' : 'unix'
  );

  return (
    <ModModal
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: (theme) => theme.zIndex.dialog }}
      {...bindDialog(popupState)}
      aria-labelledby="moderne-cli-dialog-title"
      aria-describedby="moderne-cli-dialog-description"
    >
      <ModModalHeader title="Moderne CLI" onClose={popupState.close} />
      <ModModalContent>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            mb: 2
          }}
        >
          The Moderne CLI is a multi-repository developer experience for
          automated code remediation. From the CLI, you can build and publish
          LSTs, access and run recipes, generate data tables, and easily test
          recipes working with the Moderne IDE plugin.
        </Typography>
        <TabContext value={currentTab}>
          <Tabs
            value={currentTab}
            onChange={(_event, newValue: string) => setCurrentTab(newValue)}
          >
            <Tab label="Linux / macOS" value="unix" />
            <Tab label="Windows" value="windows" />
          </Tabs>
          <ModerneTabPanel value="unix">
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                mb: 1
              }}
            >
              Open Terminal and run:
            </Typography>
            <CodeSnippet>{`curl ${cliBaseUrl}/cli | bash`}</CodeSnippet>
          </ModerneTabPanel>
          <ModerneTabPanel value="windows">
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                mb: 1
              }}
            >
              Open PowerShell and run:
            </Typography>
            <CodeSnippet>{`irm ${cliBaseUrl}/cli/windows | iex`}</CodeSnippet>
          </ModerneTabPanel>
        </TabContext>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            mt: 2
          }}
        >
          For more details and setup instructions, see the{' '}
          <Link
            external
            href="https://docs.moderne.io/user-documentation/moderne-cli/getting-started/cli-intro#installation-and-configuration"
          >
            CLI documentation
          </Link>
          .
        </Typography>
      </ModModalContent>
    </ModModal>
  );
};
